import { useCallback, useMemo, useState } from "react";
import CustomDialog from "components/shared/Dialog/CustomDialog";
import CustomSelect from "components/shared/Forms/CustomSelect";
import toast from "react-hot-toast";
import theme from "components/utils/theme";
import deviceStyles from "components/modules/Device/device.module.scss";
import sharedStyles from "components/shared/shared.module.scss";
import textStyles from "components/shared/css/text.module.scss";
import CopyIcon from "static/svgs/Copy";

const SecureBootInstallDialog = ({ open, onClose }) => {
  const fileDonwloadUrl = `${process.env.REACT_APP_SOCKET_URL}/file-download`;
  const windowsInstallCommand = `Invoke-WebRequest '${fileDonwloadUrl}/binary/getExe/secureBootScript.ps1' -OutFile \"secureBootScript.ps1\";Invoke-Command -ScriptBlock { & \"./secureBootScript.ps1\" };Remove-Item -Path \"./secureBootScript.ps1\";`;
  const linuxInstallCommand = "";

  const installOsOptions = useMemo(
    () => [
      { label: "Windows", value: "windows" },
      { label: "Linux", value: "linux" },
      { label: "Debian", value: "debian" },
    ],
    []
  );

  const installationSteps = useMemo(
    () => [
      "Open the bash terminal on the device.",
      "Copy the command from clipboard.",
      "Paste the command in the terminal and run it.",
      "Blot client will be installed on the device.",
    ],
    []
  );

  const [selectedInstallOs, setSelectedInstallOs] = useState("windows");
  const [commandCopied, setCommandCopied] = useState(false);

  const installCommand = useMemo(() => {
    return selectedInstallOs === "windows"
      ? windowsInstallCommand
      : linuxInstallCommand;
  }, [selectedInstallOs, windowsInstallCommand]);

  const handleSelectInstallOs = useCallback((event) => {
    setSelectedInstallOs(event.target.value);
    setCommandCopied(false);
  }, []);

  const handleCopyInstallCommand = useCallback(async () => {
    const isWindows = selectedInstallOs === "windows";

    if (!installCommand) {
      toast.error(
        `${
          isWindows ? "Windows" : "Linux"
        } install command will be available soon`
      );
      return;
    }

    if (typeof navigator === "undefined" || !navigator.clipboard) {
      toast.error("Clipboard access is not supported in this browser");
      return;
    }

    try {
      await navigator.clipboard.writeText(installCommand);
      toast.success(`${isWindows ? "Windows" : "Linux"} command copied`);
      setCommandCopied(true);
    } catch (error) {
      toast.error("Failed to copy command");
    }
  }, [installCommand, selectedInstallOs]);

  return (
    <CustomDialog
      title={"Installation Steps:"}
      isOpen={open}
      handleToggle={onClose}
      maxWidth={"sm"}
      fullWidth
    >
      <div className={deviceStyles.installationDialog}>
        <div className={deviceStyles.messageContainer}>
          <p className={textStyles.normal2}>Choose installation steps:</p>
          <div className={deviceStyles.installationSteps}>
            {installationSteps.map((step) => (
              <div key={step} className={deviceStyles.installationStepItem}>
                <div
                  className={`${sharedStyles.circle} ${deviceStyles.bgBlue}`}
                />
                <p className={textStyles.normal1}>{step}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <CustomSelect
            label="Select OS"
            options={installOsOptions}
            value={selectedInstallOs}
            onChange={handleSelectInstallOs}
          />
        </div>
        <div className={deviceStyles.row}>
          <p className={textStyles.formLabel}>Command:</p>
          <div
            style={{
              backgroundColor: installCommand
                ? commandCopied
                  ? theme.palette.primary.light
                  : theme.palette.primary.main
                : theme.palette.gray.light,
              cursor: installCommand ? "pointer" : "not-allowed",
            }}
            className={`${deviceStyles.copyBtn} copy-icon`}
            onClick={handleCopyInstallCommand}
          >
            <CopyIcon />
          </div>
        </div>
        <pre
          className={`${textStyles.small1} ${deviceStyles.installCommandBox}`}
        >
          {installCommand || "Linux install command will be available soon."}
        </pre>
      </div>
    </CustomDialog>
  );
};

export default SecureBootInstallDialog;
